import React from 'react';
import i18n from 'i18next';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getTranslatedQuestionLabel } from './getTranslatedQuestionLabel';
import { formatForm, NON_CONTENT_FIELD_TYPES } from './utils';
import { isEmail, isUrl } from '../../../../../attendee/invitations/utils';

import { Card, ContentCard, HeaderCard, Icon, SubTitleCard, TitleCard } from '@weezevent/nacre';

import ModerationProfileAttendeeInformationsForm from './moderationProfileAttendeeInformationsForm';

import Style from '../../moderationProfile.module.css';

export default function ModerationProfileAttendeeInformations({ attendee, organizationId, flowId, syncAttendee }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [editAttendee, setEditAttendee] = React.useState(false);

    const isFormEmpty = React.useMemo(() => {
        if (!attendee.form || attendee.form.length === 0) return true;

        // Check if there's at least one field that contains displayable content
        return !attendee.form.some(entry => !NON_CONTENT_FIELD_TYPES.includes(entry.field_type));
    }, [attendee.form]);

    const { language: locale } = i18n;
    const context = attendee.event_spam_moderation ? 'event' : 'attendee';
    const formattedAttendeeFormData = React.useMemo(() => formatForm(attendee.form, t, locale), [attendee.form, locale, t]);
    const allFieldsNotEditable = React.useMemo(() => attendee.form?.every(item => !item.allow_modification), [attendee]);

    return (
        <Card id={context} className={Style['attendee-card']}>
            <HeaderCard className={Style['attendee-cardInfos-header']}>
                <div>
                    <TitleCard title={t(`ticket.moderation.attendee.${context}Informations.title`)} />
                    <SubTitleCard title={t(`ticket.moderation.attendee.${context}Informations.subtitle`)} />
                </div>
                {!editAttendee && !isFormEmpty && !allFieldsNotEditable && !attendee.event_spam_moderation && (
                    <div className={Style['attendee-cardInfos-editCta']} onClick={() => setEditAttendee(true)}>
                        <span>{t('ticket.common.action.modify')}</span>
                        <Icon name="edit" />
                    </div>
                )}
            </HeaderCard>
            <ContentCard>
                {isFormEmpty ? (
                    <Card.EmptyState
                        icon={<Icon name="list" />}
                        title={t('ticket.common.noData')}
                        subtitle={t('ticket.moderation.attendee.formEmptyStateSubtitle')}
                        labelButton={t('ticket.moderation.attendee.formEmptyStateCta')}
                        onClick={() => {
                            history.push(`/ticket/O${organizationId}/E${attendee.event_id}/settings/forms`);
                        }}
                    />
                ) : editAttendee ? (
                    <ModerationProfileAttendeeInformationsForm
                        attendeeForm={formattedAttendeeFormData}
                        setEditAttendee={setEditAttendee}
                        organizationId={organizationId}
                        attendee={attendee}
                        flowId={flowId}
                        syncAttendee={syncAttendee}
                    />
                ) : (
                    <ul className={Style['attendee-infos-listItem']}>
                        {formattedAttendeeFormData.map((formEntry, index) => (
                            <li key={index}>
                                <span className={Style['attendee-infos-listItem-label']}>{getTranslatedQuestionLabel(formEntry, locale)}</span>
                                {isUrl(formEntry.text) && !isEmail(formEntry.text) ? (
                                    <a href={formEntry.text} className={Style['attendee-infos-listItem-answer-url']} target="_blank" rel="noopener noreferrer">
                                        {formEntry.text}
                                    </a>
                                ) : (
                                    <span className={Style['attendee-infos-listItem-answer']}>{formEntry.text}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </ContentCard>
        </Card>
    );
}
